import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import {
  EllipsisVerticalIcon,
  PenIcon,
  PlusIcon,
  Trash2Icon
} from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import RichTextField from '@/components/RichTextField';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyContent } from '@/components/ui/empty';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

interface GetDealOpportunityHighlights {
  opportunityHighlights: {
    __typename: 'OpportunityHighlightNode';
    createDate: string;
    createUid: {
      __typename: 'UserNode';
      name: string;
    };
    id: string;
    sequence: number;
    support: string;
    title: string;
  }[];
}

interface GetDealOpportunityRisks {
  opportunityRisks: {
    __typename: 'OpportunityRiskNode';
    createDate: string;
    createUid: {
      __typename: 'UserNode';
      name: string;
    };
    id: string;
    mitigant: null | string;
    risk: null | string;
    sequence: number;
  }[];
}

const GET_DEAL_OPPORTUNITY_HIGHLIGHTS = gql`
  query DealOpportunityHighlights($deal_opportunity_id: ID!) {
    opportunityHighlights(dealOpportunityId: $deal_opportunity_id) {
      id
      title
      support
      createDate
      createUid {
        name
      }
      sequence
    }
  }
`;

const GET_DEAL_OPPORTUNITY_RISKS = gql`
  query DealOpportunityRisks($deal_opportunity_id: ID!) {
    opportunityRisks(dealOpportunityId: $deal_opportunity_id) {
      id
      risk
      mitigant
      createDate
      createUid {
        name
      }
      sequence
    }
  }
`;

const CREATE_DEAL_OPPORTUNITY_HIGHLIGHT = gql`
  mutation CreateDealOpportunityHighlight(
    $opportunity_id: ID!
    $title: String!
    $support: String
  ) {
    createOpportunityHighlight(
      opportunityId: $opportunity_id
      values: { title: $title, support: $support }
    ) {
      ok
    }
  }
`;

const CREATE_DEAL_OPPORTUNITY_RISK = gql`
  mutation CreateDealOpportunityRisk(
    $deal_opportunity_id: ID!
    $risk: String!
    $mitigant: String
  ) {
    createOpportunityRisk(
      opportunityId: $deal_opportunity_id
      values: { risk: $risk, mitigant: $mitigant }
    ) {
      ok
    }
  }
`;

const DELETE_OPPORTUNITY_HIGHLIGHT = gql`
  mutation DeleteDealOpportunityHighlight($id: ID!) {
    deleteOpportunityHighlight(id: $id) {
      ok
    }
  }
`;

const DELETE_OPPORTUNITY_RISK = gql`
  mutation DeleteDealOpportunityRisk($id: ID!) {
    deleteOpportunityRisk(id: $id) {
      ok
    }
  }
`;

const UPDATE_OPPORTUNITY_HIGHLIGHT = gql`
  mutation UpdateDealOpportunityHighlight(
    $title: String!
    $support: String
    $id: ID!
  ) {
    updateOpportunityHighlight(
      id: $id
      values: { title: $title, support: $support }
    ) {
      ok
    }
  }
`;

const UPDATE_OPPORTUNITY_RISK = gql`
  mutation UpdateDealOpportunityRisk(
    $id: ID!
    $risk: String!
    $mitigant: String
  ) {
    updateOpportunityRisk(
      id: $id
      values: { risk: $risk, mitigant: $mitigant }
    ) {
      ok
    }
  }
`;

// Shared shape for table rows (highlights and risks)
interface DealSectionItem {
  createDate: string;
  createUid: { name: string };
  id: string;
  primaryText: string;
  secondaryText: string;
}

const CONFIRM_DELETE_DESCRIPTION =
  'This action cannot be undone. This will permanently delete this item.';

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Reusable section: table + new dialog + edit dialog + delete confirm
interface DealItemSectionProps {
  buildCreateVariables: (
    title: string,
    secondaryHtml: null | string
  ) => Record<string, unknown>;
  buildUpdateVariables: (
    id: string,
    title: string,
    secondaryHtml: null | string
  ) => Record<string, unknown>;
  createMutation: (options: {
    refetchQueries: { query: ReturnType<typeof gql>; variables: object }[];
    variables: Record<string, unknown>;
  }) => Promise<unknown>;
  deleteMutation: (options: {
    refetchQueries: { query: ReturnType<typeof gql>; variables: object }[];
    variables: { id: string };
  }) => Promise<unknown>;
  editDialogTitle: string;
  isCreating: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  items: DealSectionItem[];
  newButtonLabel: string;
  newDialogTitle: string;
  primaryColumnLabel: string;
  refetchQuery: ReturnType<typeof gql>;
  refetchVariables: { deal_opportunity_id: string };
  secondaryColumnLabel: string;
  sectionTitle: string;
  updateMutation: (options: {
    refetchQueries: { query: ReturnType<typeof gql>; variables: object }[];
    variables: Record<string, unknown>;
  }) => Promise<unknown>;
}

export function HighlightsSection({
  opportunityId
}: {
  opportunityId: string;
}) {
  const { data, loading } = useQuery<
    GetDealOpportunityHighlights,
    { deal_opportunity_id: string }
  >(GET_DEAL_OPPORTUNITY_HIGHLIGHTS, {
    variables: { deal_opportunity_id: opportunityId }
  });

  const [createHighlight, { loading: isCreating }] = useMutation(
    CREATE_DEAL_OPPORTUNITY_HIGHLIGHT
  );
  const [updateHighlight, { loading: isUpdating }] = useMutation(
    UPDATE_OPPORTUNITY_HIGHLIGHT
  );
  const [deleteHighlight, { loading: isDeleting }] = useMutation(
    DELETE_OPPORTUNITY_HIGHLIGHT
  );

  const items: DealSectionItem[] = (data?.opportunityHighlights ?? []).map(
    h => ({
      createDate: h.createDate,
      createUid: h.createUid,
      id: h.id,
      primaryText: h.title,
      secondaryText: h.support
    })
  );

  if (loading) {
    return (
      <div>
        <h2 className="mb-3 text-lg font-semibold">Highlights</h2>
        <div className="flex justify-center py-8">
          <Spinner className="size-6 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <DealItemSection
      buildCreateVariables={(title, support) => ({
        opportunity_id: opportunityId,
        support: support ?? undefined,
        title
      })}
      buildUpdateVariables={(id, title, support) => ({
        id,
        support: support ?? undefined,
        title
      })}
      createMutation={opts =>
        createHighlight({
          refetchQueries: opts.refetchQueries,
          variables: opts.variables as {
            opportunity_id: string;
            support?: string;
            title: string;
          }
        })
      }
      deleteMutation={opts =>
        deleteHighlight({
          refetchQueries: opts.refetchQueries,
          variables: opts.variables
        })
      }
      editDialogTitle="Edit Highlight"
      isCreating={isCreating}
      isDeleting={isDeleting}
      isUpdating={isUpdating}
      items={items}
      newButtonLabel="New Highlight"
      newDialogTitle="New Highlight"
      primaryColumnLabel="Title"
      refetchQuery={GET_DEAL_OPPORTUNITY_HIGHLIGHTS}
      refetchVariables={{ deal_opportunity_id: opportunityId }}
      secondaryColumnLabel="Support"
      sectionTitle="Highlights"
      updateMutation={opts =>
        updateHighlight({
          refetchQueries: opts.refetchQueries,
          variables: opts.variables as {
            id: string;
            support?: string;
            title: string;
          }
        })
      }
    />
  );
}

export function RisksSection({ opportunityId }: { opportunityId: string }) {
  const { data, loading } = useQuery<
    GetDealOpportunityRisks,
    { deal_opportunity_id: string }
  >(GET_DEAL_OPPORTUNITY_RISKS, {
    variables: { deal_opportunity_id: opportunityId }
  });

  const [createRisk, { loading: isCreating }] = useMutation(
    CREATE_DEAL_OPPORTUNITY_RISK
  );
  const [updateRisk, { loading: isUpdating }] = useMutation(
    UPDATE_OPPORTUNITY_RISK
  );
  const [deleteRisk, { loading: isDeleting }] = useMutation(
    DELETE_OPPORTUNITY_RISK
  );

  const items: DealSectionItem[] = (data?.opportunityRisks ?? []).map(r => ({
    createDate: r.createDate,
    createUid: r.createUid,
    id: r.id,
    primaryText: r.risk ?? '',
    secondaryText: r.mitigant ?? ''
  }));

  if (loading) {
    return (
      <div>
        <h2 className="mb-3 text-lg font-semibold">Risk</h2>
        <div className="flex justify-center py-8">
          <Spinner className="size-6 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <DealItemSection
      buildCreateVariables={(title, mitigant) => ({
        deal_opportunity_id: opportunityId,
        mitigant: mitigant ?? undefined,
        risk: title
      })}
      buildUpdateVariables={(id, title, mitigant) => ({
        id,
        mitigant: mitigant ?? undefined,
        risk: title
      })}
      createMutation={opts =>
        createRisk({
          refetchQueries: opts.refetchQueries,
          variables: opts.variables as {
            deal_opportunity_id: string;
            mitigant?: string;
            risk: string;
          }
        })
      }
      deleteMutation={opts =>
        deleteRisk({
          refetchQueries: opts.refetchQueries,
          variables: opts.variables
        })
      }
      editDialogTitle="Edit Risk"
      isCreating={isCreating}
      isDeleting={isDeleting}
      isUpdating={isUpdating}
      items={items}
      newButtonLabel="New Risk"
      newDialogTitle="New Risk"
      primaryColumnLabel="Risk"
      refetchQuery={GET_DEAL_OPPORTUNITY_RISKS}
      refetchVariables={{ deal_opportunity_id: opportunityId }}
      secondaryColumnLabel="Mitigant"
      sectionTitle="Risk"
      updateMutation={opts =>
        updateRisk({
          refetchQueries: opts.refetchQueries,
          variables: opts.variables as {
            id: string;
            mitigant?: string;
            risk: string;
          }
        })
      }
    />
  );
}

function DealItemSection({
  buildCreateVariables,
  buildUpdateVariables,
  createMutation,
  deleteMutation,
  editDialogTitle,
  isCreating,
  isDeleting,
  isUpdating,
  items,
  newButtonLabel,
  newDialogTitle,
  primaryColumnLabel,
  refetchQuery,
  refetchVariables,
  secondaryColumnLabel,
  sectionTitle,
  updateMutation
}: DealItemSectionProps) {
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editId, setEditId] = useState<null | string>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<null | string>(null);
  const [titleInvalidNew, setTitleInvalidNew] = useState(false);
  const [titleInvalidEdit, setTitleInvalidEdit] = useState(false);

  const newSecondaryRef = useRef<Editor>(null);
  const editSecondaryRef = useRef<Editor>(null);

  const refetch = [{ query: refetchQuery, variables: refetchVariables }] as {
    query: ReturnType<typeof gql>;
    variables: object;
  }[];

  const handleNewSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const raw = formData.get('title-field');
    const title = (typeof raw === 'string' ? raw : '').trim();
    if (!title) {
      setTitleInvalidNew(true);
      return;
    }
    if (!newSecondaryRef.current) {
      toast.error('Unable to read field');
      return;
    }
    try {
      await createMutation({
        refetchQueries: refetch,
        variables: buildCreateVariables(
          title,
          newSecondaryRef.current.isEmpty
            ? null
            : newSecondaryRef.current.getHTML()
        )
      });
      setNewDialogOpen(false);
      setTitleInvalidNew(false);
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  const handleEditSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
    itemId: string
  ) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const raw = formData.get('title-field');
    const title = (typeof raw === 'string' ? raw : '').trim();
    if (!title) {
      setTitleInvalidEdit(true);
      return;
    }
    try {
      await updateMutation({
        refetchQueries: refetch,
        variables: buildUpdateVariables(
          itemId,
          title,
          editSecondaryRef.current?.isEmpty
            ? null
            : (editSecondaryRef.current?.getHTML() ?? null)
        )
      });
      setTitleInvalidEdit(false);
      setEditId(null);
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      await deleteMutation({
        refetchQueries: refetch,
        variables: { id: itemId }
      });
      setConfirmDeleteOpen(false);
      setDeleteId(null);
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  return (
    <div>
      <div className="mb-3 flex h-9 items-center justify-between">
        <h2 className="text-lg font-semibold">{sectionTitle}</h2>
        <Dialog
          onOpenChange={open => {
            setNewDialogOpen(open);
            if (!open) setTitleInvalidNew(false);
          }}
          open={newDialogOpen}
        >
          {items.length !== 0 && (
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                {newButtonLabel}
              </Button>
            </DialogTrigger>
          )}
          <DialogContent aria-describedby={undefined}>
            <DialogHeader className="gap-1">
              <div className="flex items-center gap-2">
                <DialogTitle>{newDialogTitle}</DialogTitle>
                <Badge variant="yellow">Unsaved</Badge>
              </div>
            </DialogHeader>
            <DialogBody>
              <form
                id={`new-${sectionTitle.toLowerCase().replace(/\s/g, '-')}-form`}
                noValidate
                onSubmit={e => {
                  void handleNewSubmit(e);
                }}
              >
                <FieldGroup>
                  <Field data-invalid={titleInvalidNew}>
                    <FieldLabel
                      className="inline-block"
                      htmlFor="title-field"
                    >
                      Title <span className="text-red-500">*</span>
                    </FieldLabel>
                    <Input
                      aria-invalid={titleInvalidNew}
                      id="title-field"
                      name="title-field"
                      placeholder="Enter Value"
                      required
                    />
                    {titleInvalidNew && (
                      <FieldError>Please enter a value</FieldError>
                    )}
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="secondary-field">
                      {secondaryColumnLabel}
                    </FieldLabel>
                    <RichTextField
                      defaultValue=""
                      id="secondary-field"
                      ref={newSecondaryRef}
                    />
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isCreating}
                form={`new-${sectionTitle.toLowerCase().replace(/\s/g, '-')}-form`}
                type="submit"
              >
                {isCreating && <Spinner />}
                Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-card rounded-md border p-5">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{primaryColumnLabel}</TableHead>
              <TableHead>{secondaryColumnLabel}</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Created On</TableHead>
              <TableHead className="bg-card sticky right-0 z-10 w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  className="p-0"
                  colSpan={5}
                >
                  <Empty className="px-0! pt-5! pb-0!">
                    <EmptyContent>
                      <Button
                        onClick={() => {
                          setNewDialogOpen(true);
                        }}
                      >
                        <PlusIcon />
                        {newButtonLabel}
                      </Button>
                    </EmptyContent>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item: DealSectionItem) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    {item.primaryText}
                  </TableCell>
                  <TableCell className="text-foreground">
                    {stripHtml(item.secondaryText)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar>
                        <AvatarFallback className="text-xs">
                          {item.createUid.name
                            .split(' ')
                            .map(p => p[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-foreground text-sm font-medium">
                        {item.createUid.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-foreground">
                    {item.createDate.split('T')[0]}
                  </TableCell>
                  <TableCell className="bg-card sticky right-0 z-10 w-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="bg-card shadow-xs"
                          size="icon-sm"
                          variant="ghost"
                        >
                          <EllipsisVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditId(item.id);
                          }}
                        >
                          <PenIcon />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            setDeleteId(item.id);
                            setConfirmDeleteOpen(true);
                          }}
                          variant="destructive"
                        >
                          <Trash2Icon />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Dialog
                      onOpenChange={open => {
                        if (!open) {
                          setEditId(null);
                          setTitleInvalidEdit(false);
                        }
                      }}
                      open={editId === item.id}
                    >
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            {editDialogTitle}
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>
                        <DialogBody>
                          <form
                            id={`edit-${item.id}-form`}
                            noValidate
                            onSubmit={e => {
                              void handleEditSubmit(e, item.id);
                            }}
                          >
                            <FieldGroup>
                              <Field data-invalid={titleInvalidEdit}>
                                <FieldLabel
                                  className="inline-block"
                                  htmlFor="title-field"
                                >
                                  Title <span className="text-red-500">*</span>
                                </FieldLabel>
                                <Input
                                  aria-invalid={titleInvalidEdit}
                                  defaultValue={item.primaryText}
                                  id="title-field"
                                  name="title-field"
                                  placeholder="Enter Value"
                                  required
                                />
                                {titleInvalidEdit && (
                                  <FieldError>Please enter a value</FieldError>
                                )}
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="secondary-field">
                                  {secondaryColumnLabel}
                                </FieldLabel>
                                <RichTextField
                                  defaultValue={item.secondaryText}
                                  id="secondary-field"
                                  ref={editSecondaryRef}
                                />
                              </Field>
                            </FieldGroup>
                          </form>
                        </DialogBody>
                        <DialogFooter>
                          <Button
                            disabled={isUpdating}
                            form={`edit-${item.id}-form`}
                            type="submit"
                          >
                            {isUpdating && <Spinner />}
                            Confirm
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <AlertDialog
                      onOpenChange={setConfirmDeleteOpen}
                      open={confirmDeleteOpen && deleteId === item.id}
                    >
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {CONFIRM_DELETE_DESCRIPTION}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            disabled={isDeleting}
                            onClick={() => {
                              if (deleteId) void handleDelete(deleteId);
                            }}
                            variant="destructive"
                          >
                            {isDeleting && <Spinner />}
                            Continue
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const InvestmentsDirectHighlightsAndRisks = () => {
  const { id, loading } = useOutletContext();

  if (loading) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="space-y-5">
            <HighlightsSection opportunityId={id} />
            <RisksSection opportunityId={id} />
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectHighlightsAndRisks;
